import { stringify } from 'qs'
import type { NitroFetchOptions } from 'nitropack'
import { useKeysToCamelCase } from '@stakeholderlabs/shared'
import { useRequestHeaders } from '#app'
import { createError } from '#imports'
import { useHandleException } from '~/composables/useHandleException'

interface ClientSettings {
  withoutCookies?: boolean
}

const isDev = process.env.NODE_ENV === 'development'

export function useClient<T>(
  baseUrl: string,
  url: string,
  options: NitroFetchOptions<'json'> = {},
  settings: ClientSettings = {},
): Promise<T> {
  options.baseURL = baseUrl
  options.onRequest = ({ options }) => {
    if (options.query) {
      const convertedOptions = new URLSearchParams(stringify(options.query))
      options.query = Object.fromEntries(convertedOptions)
    }
  }

  let headers = Object.assign({
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/json, text/plain, */*',
  })

  if (!isDev) {
    headers['X-Forwarded-Host'] = useRequestHeaders(['host']).host
  }

  if (!settings?.withoutCookies) {
    headers = Object.assign(headers, useRequestHeaders(['cookie']) as Record<string, string>)
  }

  if (options.headers) {
    options.headers = Object.assign({}, headers, options.headers)
  }
  else {
    options.headers = headers
  }

  options.onResponseError = ({ response }): Promise<void> | void => {
    if (response) {
      if (response.status === 404) {
        return
      }
      if ([400, 429].includes(response.status)) {
        if (import.meta.client) {
          alert('Something went wrong. Please try again.')
        }
      }

      if (response.status === 403) {
        throw createError({
          status: response.status,
          statusText: 'You do not have permission to perform this action.',
        })
      }
      else if (response.status >= 500) {
        try {
          useHandleException(response)
        }
        catch {
          // eslint-disable-next-line no-console
          console.error('Sentry instance is not available', response)
        }
      }
    }
  }

  options.parseResponse = (responseText: string) => {
    return useKeysToCamelCase(JSON.parse(responseText))
  }

  return $fetch(url, <NitroFetchOptions<'json'>>options)
}
